import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import { Card, CardHeader, Badge, Button, Spinner } from '@tkpod/ui';

interface ConnectionSummary {
  id: string;
  organizationId: string;
  type: string;
  name: string;
  status: string;
  shopDomain: string | null;
  syncStatus: string;
  lastSyncAt: string | null;
  productCount: number;
  orderCount: number;
  createdAt: string;
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  ERROR: 'danger',
  DISCONNECTED: 'default',
};

const SYNC_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'default' | 'info'> = {
  SYNCED: 'success',
  SYNCING: 'info',
  PENDING: 'default',
  DRIFT_DETECTED: 'warning',
  ERROR: 'danger',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function StoresPage() {
  const api = useApiClient();

  const connectionsQuery = useQuery({
    queryKey: ['organization', 'connections'],
    queryFn: () => api.get<ConnectionSummary[]>('/api/v1/organizations/current/connections'),
  });

  const connections = connectionsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <Card padding="none">
        <div className="px-5 py-4">
          <CardHeader
            title="Connected Stores"
            description="Stores connected via Shopify are automatically provisioned when the app is installed."
          />
        </div>

        {connectionsQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : connectionsQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-sm text-text-secondary">Failed to load store connections.</p>
            <Button variant="outline" size="sm" onClick={() => connectionsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : connections.length === 0 ? (
          <div className="py-20 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
              <svg
                className="h-6 w-6 text-text-tertiary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-text-primary">No stores connected</p>
            <p className="mt-1 text-sm text-text-tertiary">
              Install the Transfer Kingdom POD app on your Shopify store to automatically connect it.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-t border-border bg-surface-secondary/40">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Store
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Platform
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Status
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Sync
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Products
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Orders
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Connected
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {connections.map((conn) => (
                  <tr key={conn.id} className="transition-colors hover:bg-surface-secondary/60">
                    <td className="px-5 py-3">
                      <div>
                        <p className="font-medium text-text-primary">{conn.name}</p>
                        {conn.shopDomain && (
                          <p className="mt-0.5 text-xs text-text-tertiary">{conn.shopDomain}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <Badge variant="default" size="sm">
                        {conn.type}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <Badge variant={STATUS_VARIANT[conn.status] ?? 'default'} size="sm">
                        {conn.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <Badge variant={SYNC_STATUS_VARIANT[conn.syncStatus] ?? 'default'} size="sm">
                        {conn.syncStatus.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="hidden px-5 py-3 text-right text-text-secondary sm:table-cell">
                      {conn.productCount}
                    </td>
                    <td className="hidden px-5 py-3 text-right text-text-secondary sm:table-cell">
                      {conn.orderCount}
                    </td>
                    <td className="hidden px-5 py-3 text-[12px] text-text-tertiary lg:table-cell">
                      {formatDate(conn.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
